/**
 * buildReport — KY Report 데이터 엔진 (public/report.html DATA 레이어에서 이식).
 *  raw = { studentCard, examsPlanned, engine, genetics, conceptsMeta } → 뷰모델 d.
 *  순수 함수(DOM 무의존). 원본: public/report.html (===DATA_START===~===DATA_END===).
 *  원본 대비 변경: weeklySeries가 무활동 주를 zero-fill(원본은 활동 주만 담아 최장 연속이 항상 활동 주 총수와 동일해지는 결함).
 *  2026-07-04 세이프가드: 처방 풀 = [엔진 취약 direct<0.5·3회+] ∪ [만성 취약 acc≤50%·10회+]
 *  − [숙달 acc≥85%·10회+]. 우선순위 = (0.5+0.5·√(impact/impactMax)) × W² × (0.5+0.5·st).
 *  2026-07-06 recency(C3 비대칭·순위항만, 캘리브레이션 승인): W = 0.3(1−direct)+0.7(1−accW), accW = 최근 하락
 *  시만 lifetime을 낮추는 비대칭 nudge(회복 시 accW=lifetime → 강등 없음). 풀 게이트는 lifetime 불변
 *  (만성 회복 개념 축출=2026-07-04 '회복 착시' 원천차단). 선택 매스컨셉은 어댑터가 우선 정렬+전체 채움으로 처리.
 *  상세: docs/report/ky-report-reference.md(종합) · docs/archive/rx-top5-safeguard-filter-plan.md · docs/report/dna-report-backend-handoff.html §7.5.
 */
/* eslint-disable */
// ───────── 수학 유틸 ─────────
function safeDivide(a, b, fallback = 0) { return (b === 0 || b == null) ? fallback : a / b; }
function sum(arr) { return arr.reduce((s, x) => s + (x || 0), 0); }
function mean(arr) { return arr.length ? sum(arr) / arr.length : 0; }
function stddev(arr) { if (arr.length < 2) return 0; const m = mean(arr); return Math.sqrt(mean(arr.map(x => (x - m) ** 2))); }
function clamp(v, min, max) { return Math.max(min, Math.min(max, v)); }
function percentile(arr, p) {
  if (!arr.length) return 0;
  const s = [...arr].sort((a, b) => a - b);
  const idx = clamp(Math.round((p / 100) * (s.length - 1)), 0, s.length - 1);
  return s[idx];
}

// ───────── 포맷 ─────────
function pct1(v) { return (v == null || isNaN(v)) ? '–' : (v * 100).toFixed(1) + '%'; }
function pct0(v) { return (v == null || isNaN(v)) ? '–' : Math.round(v * 100) + '%'; }
function num(n) { return (n == null) ? '–' : n.toLocaleString('ko-KR'); }
function formatDateKR(iso) {
  if (!iso) return '–';
  const d = new Date(iso);
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;
}
function ymd(iso) { const d = new Date(iso); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; }
function daysBetween(a, b) { return Math.round((new Date(b) - new Date(a)) / 86400000); }
function monthKey(iso) { const d = new Date(iso); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; }
function weekKey(iso) {
  const d = new Date(iso); const onejan = new Date(d.getFullYear(), 0, 1);
  const week = Math.ceil((((d - onejan) / 86400000) + onejan.getDay() + 1) / 7);
  return `${d.getFullYear()}-W${String(week).padStart(2, '0')}`;
}

// ───────── 진단 룰 ─────────
// scores = [[tc,tw],[d1c,d1w],...,[d5c,d5w]]
function diagnoseDifficultyPattern(scores) {
  const totalWrong = scores[0][1];
  if (totalWrong === 0) return 'mastered';
  const easyWrong = (scores[1]?.[1] || 0) + (scores[2]?.[1] || 0);
  const hardWrong = (scores[4]?.[1] || 0) + (scores[5]?.[1] || 0);
  if (easyWrong / totalWrong >= 0.4) return 'basic';        // 기초형
  if (hardWrong / totalWrong >= 0.4) return 'application';  // 응용형
  return 'mixed';                                            // 혼합형
}
// 난도 천장: 정답률이 마지막으로 50% 이상을 유지하는 난이도 레벨
function calcDifficultyCeiling(diffBreakdown) {
  let ceiling = 0;
  for (const b of diffBreakdown) {
    const att = b.c + b.w;
    if (att >= 2 && safeDivide(b.c, att) >= 0.5) ceiling = b.d;
  }
  return ceiling;
}

// ───────── 스코어 ─────────
function calcHealthScore(m) {
  // 0.30 전체정답률 + 0.20 최근30일정확도 + 0.15 신선도 + 0.20 평균최근성 + 0.15 평균안정성
  return 100 * (0.30 * m.overallAcc + 0.20 * m.last30Acc + 0.15 * m.freshness + 0.20 * m.avgR + 0.15 * m.avgStab);
}
function calcRiskScore(m) {
  // 0.40 취약비중 + 0.35 정체취약비중 + 0.25 (1-평균안정성)
  return 100 * (0.40 * m.weakShare + 0.35 * m.staleWeakShare + 0.25 * (1 - m.avgStab));
}
function calcConsistencyScore(monthlySeries) {
  const accs = monthlySeries.filter(m => m.attempts > 0).map(m => m.accuracy);
  const avg = mean(accs);
  if (accs.length < 2 || avg === 0) return null;
  const cv = stddev(accs) / avg;
  return clamp((1 - cv) * 100, 0, 100);
}

// ───────── 메인 빌더 ─────────
function buildReport(raw) {
  const { studentCard, examsPlanned, engine, genetics, conceptsMeta } = raw;
  const V = engine.v_scale || 10000;

  // 정규화: 인덱스들
  const engineIdx = {};
  engine.concept_index.forEach((cid, i) => { engineIdx[cid] = i; });
  const getEng = (cid, field) => { const i = engineIdx[cid]; return (i == null) ? null : engine[field][i]; };

  const conceptMetaById = {};
  conceptsMeta.forEach(m => { conceptMetaById[m.concept_id] = m; });

  const stampMetaById = {};
  examsPlanned.forEach(s => { stampMetaById[s.stamp_id] = s; });

  // flatAttempts: 문제 단위 평탄화
  const flatAttempts = [];
  genetics.stamps.forEach(stamp => {
    stamp.exams.forEach(exam => {
      const ps = exam.pids_solved || {};
      Object.entries(ps).forEach(([pid, val]) => {
        const [diff, correct, cids] = val;
        flatAttempts.push({
          pid,
          cids: [...new Set(cids || [])],
          correct: !!correct,
          created_at: exam.created_at,
          exam_type: exam.exam_type || 'unknown',
          difficulty: diff,                 // 문제 난도 1~5
          exam_round: exam.exam_round,
          length: exam.length,
          stamp_id: stamp.stamp_id,
          tree_name: stamp.tree_name,
        });
      });
    });
  });

  // 기간
  const attemptDates = flatAttempts.map(a => a.created_at).filter(Boolean).sort();
  const periodStart = studentCard.created_at || attemptDates[0];
  const periodEnd = attemptDates[attemptDates.length - 1];
  const endMs = new Date(periodEnd).getTime();

  // ── Impact / Pair ──
  const pidsByConcept = {}, failedPidsByConcept = {};
  flatAttempts.forEach(({ pid, correct, cids }) => {
    if (!cids.length) return;
    cids.forEach(cid => {
      (pidsByConcept[cid] ||= new Set()).add(pid);
      if (!correct) (failedPidsByConcept[cid] ||= new Set()).add(pid);
    });
  });
  // ── recency: 개념별 최근 K회 정답률(2026-07-06, 우선순위 W항 비대칭 nudge 전용 — 풀 게이트 불변) ──
  //   총 시도 ≤ K면 '최근 K'가 곧 전체라 recency 신호가 없다 → recentAcc=null → nudge 0(저N 튐 구조적 차단,
  //   lifetime 집계와의 수치 일치에 비의존). 시도 > K일 때만 최근 K개의 정답률을 별도 산출한다.
  const RX_RECENT_K = 10, RX_RECENCY_LAMBDA = 1, RX_RECENCY_DEADBAND = 0.05;
  const attemptsByConcept = {};
  flatAttempts.forEach(a => {
    if (!a.cids.length) return;
    a.cids.forEach(cid => { (attemptsByConcept[cid] ||= []).push(a); });
  });
  const recentAccOf = (cid) => {
    const arr = attemptsByConcept[cid];
    if (!arr || arr.length <= RX_RECENT_K) return null;
    const recent = arr.slice()
      .sort((x, y) => String(x.created_at || '').localeCompare(String(y.created_at || '')))
      .slice(-RX_RECENT_K);
    return recent.filter(a => a.correct).length / recent.length;
  };

  const pairCount = {};
  flatAttempts.filter(a => !a.correct && a.cids.length >= 2).forEach(({ cids }) => {
    const s = [...cids].sort((a, b) => a - b);
    for (let i = 0; i < s.length; i++)
      for (let j = i + 1; j < s.length; j++)
        pairCount[`${s[i]}_${s[j]}`] = (pairCount[`${s[i]}_${s[j]}`] || 0) + 1;
  });
  function topPartners(cid, k = 3) {
    const out = [];
    Object.entries(pairCount).forEach(([key, n]) => {
      const [a, b] = key.split('_').map(Number);
      if (a === cid) out.push({ cid: b, n }); else if (b === cid) out.push({ cid: a, n });
    });
    return out.sort((x, y) => y.n - x.n).slice(0, k);
  }

  // ── 개념별 마지막 출제일 / 추세 ──
  const lastSeen = {}, recentSplit = {};
  flatAttempts.forEach(a => {
    a.cids.forEach(cid => {
      if (!lastSeen[cid] || a.created_at > lastSeen[cid]) lastSeen[cid] = a.created_at;
      const bucket = (endMs - new Date(a.created_at).getTime()) <= 30 * 86400000 ? 'recent' : 'prev';
      (recentSplit[cid] ||= { recent: [0, 0], prev: [0, 0] });
      recentSplit[cid][bucket][a.correct ? 0 : 1]++;
    });
  });
  function trendBadge(cid) {
    const r = recentSplit[cid]; if (!r) return { symbol: '→', delta: 0 };
    const ra = r.recent[0] + r.recent[1], pa = r.prev[0] + r.prev[1];
    if (ra < 3 || pa < 3) return { symbol: '→', delta: 0 };
    const delta = safeDivide(r.recent[0], ra) - safeDivide(r.prev[0], pa);
    return { symbol: delta > 0.03 ? '▲' : delta < -0.03 ? '▼' : '→', delta };
  }
  // 개념별 최근 12시험 정답률 스파크라인용
  const conceptExamHist = {};
  genetics.stamps.forEach(stamp => stamp.exams.forEach(exam => {
    const ps = exam.pids_solved || {};
    const tally = {};
    Object.values(ps).forEach(([d, correct, cids]) => {
      [...new Set(cids || [])].forEach(cid => { (tally[cid] ||= [0, 0])[correct ? 0 : 1]++; });
    });
    Object.entries(tally).forEach(([cid, [c, w]]) => {
      (conceptExamHist[cid] ||= []).push({ at: exam.created_at, acc: safeDivide(c, c + w) });
    });
  }));
  Object.values(conceptExamHist).forEach(arr => arr.sort((a, b) => (a.at < b.at ? -1 : 1)));

  // ── conceptsById (학습 개념 = genetics.concepts 키, 410개) ──
  const conceptsById = {};
  Object.entries(genetics.concepts).forEach(([cidStr, scores]) => {
    const cid = Number(cidStr);
    const meta = conceptMetaById[cid] || {};
    const correct = scores[0][0], wrong = scores[0][1], attempts = correct + wrong;
    const accuracy = safeDivide(correct, attempts, null);
    const diffBreakdown = [1, 2, 3, 4, 5].map(d => {
      const c = scores[d]?.[0] || 0, w = scores[d]?.[1] || 0;
      return { d, c, w, acc: safeDivide(c, c + w, null) };
    });
    const stabilityRaw = getEng(cid, 'stability');
    const tb = trendBadge(cid);
    conceptsById[cid] = {
      cid,
      name: meta.concept_name || `개념 ${cid}`,
      mathType: meta.math_type || 'AL',
      conceptType: meta.concept_type || null,
      order: meta.order ?? 9999,
      tags: meta.tags || [],
      descriptions: meta.descriptions || '',
      isOutdated: !!meta.is_outdated,
      hasMeta: !!conceptMetaById[cid],
      correct, wrong, attempts, accuracy,
      diffBreakdown,
      diffPattern: diagnoseDifficultyPattern(scores),
      ceiling: calcDifficultyCeiling(diffBreakdown),
      rStatus: getEng(cid, 'r_status'),
      hStatus: getEng(cid, 'h_status'),
      stability: stabilityRaw,
      stabilityNorm: stabilityRaw == null ? null : stabilityRaw / V,
      avgDiff: getEng(cid, 'avg_diff'),
      avgDiffN: getEng(cid, 'avg_diff_n'),
      impactScore: pidsByConcept[cid]?.size || 0,
      unlockN: failedPidsByConcept[cid]?.size || 0,
      recentAcc: recentAccOf(cid),
      lastSeenAt: lastSeen[cid] || null,
      staleDays: lastSeen[cid] ? daysBetween(lastSeen[cid], periodEnd) : null,
      trendBadge: tb.symbol,
      trendDelta: tb.delta,
      sparkline: (conceptExamHist[cid] || []).slice(-12).map(h => h.acc),
      topPairPartners: topPartners(cid),
    };
  });
  const concepts = Object.values(conceptsById);

  // engine에만 있고 meta 없는 개념
  const metaMissingConceptIds = engine.concept_index.filter(cid => !conceptMetaById[cid]);

  // ── 분류/버킷 ──
  const learnedCount = concepts.length;                  // 410
  const totalConceptSpace = engine.concept_index.length; // 717
  const unlearnedCount = totalConceptSpace - learnedCount;

  const bandOf = (c) => c.accuracy == null ? 'unlearned'
    : c.accuracy >= 0.85 ? 'mastered'
    : c.accuracy >= 0.70 ? 'mid' : 'weak';
  const byBand = { mastered: [], mid: [], weak: [], unlearned: [] };
  concepts.forEach(c => byBand[bandOf(c)].push(c));

  const totalWrongAll = sum(concepts.map(c => c.wrong));
  const masteryLayers = {
    byCount: {
      mastered: byBand.mastered.length,
      mid: byBand.mid.length,
      weak: byBand.weak.length,
      unlearned: unlearnedCount,
    },
    byWrongShare: {
      masteredShare: safeDivide(sum(byBand.mastered.map(c => c.wrong)), totalWrongAll),
      midShare: safeDivide(sum(byBand.mid.map(c => c.wrong)), totalWrongAll),
      weakShare: safeDivide(sum(byBand.weak.map(c => c.wrong)), totalWrongAll),
    },
  };

  const almostMastered = concepts.filter(c => c.attempts >= 5 && c.accuracy != null && c.accuracy >= 0.75 && c.accuracy < 0.85)
    .sort((a, b) => b.accuracy - a.accuracy);
  const recoverableWrongCount = sum(almostMastered.map(c => c.wrong));
  const staleWeak = byBand.weak.filter(c => c.staleDays != null && c.staleDays >= 45);

  // ── 가변 목표치 / 상승 잠재치 (weakRanked 생성 전에 개념 객체에 부여) ──
  function tagMasteredMean(tag) {
    const xs = byBand.mastered.filter(c => (c.tags[0] || 'unknown') === tag).map(c => c.accuracy);
    return xs.length ? mean(xs) : 0.85;
  }
  concepts.forEach(c => {
    c.targetAcc = clamp(tagMasteredMean(c.tags[0] || 'unknown'), 0.80, 0.95);
    c.gainPotential = Math.max((c.targetAcc - (c.accuracy ?? 0)), 0) * c.attempts;
  });
  const totalGainPotential = sum(concepts.map(c => c.gainPotential));

  // 엔진 정규화(0~1): 직접=(r+V)/2V(중립 0.5, 음수 r의 깊이 구분) — 게이지(avgR)·어댑터 표시와 동일 규칙.
  // 연관(h)은 2026-07-04 3축 분석에서 '약함과 무상관 노이즈'로 실증돼 우선순위에서 제외(카드 표시 전용).
  const hCeil = percentile(concepts.filter(c => c.hStatus != null).map(c => c.hStatus), 90) || V;
  const directOf = (c) => (c.rStatus == null ? null : clamp((c.rStatus + V) / (2 * V), 0, 1));

  // 처방 풀(세이프가드 2종 — docs/archive/rx-top5-safeguard-filter-plan.md §3.1):
  //  A. 엔진 취약: direct<0.5(⟺ r<0) & 시도 3회+
  //  B. 만성 취약 강제 포함: 정답률≤50% & 시도 10회+ — r이 마지막 정답으로 +V 포화돼도 포함(예: acc 38%/116회·direct 0.99)
  //  C. 숙달 제외: 정답률≥85% & 시도 10회+ — 마지막 1회 오답發 r 급락 착시 차단(예: acc 91%/11회·direct 0.08)
  //  근거(3축 분석): 포화 r의 부호 = 마지막 시도 정오(97.9~100% 일치)라 r 단독 게이트는 만성 취약을 배제하고 준숙달을 편입시킴.
  const inRxPool = (c) => {
    if (c.accuracy != null && c.accuracy >= 0.85 && c.attempts >= 10) return false;
    if (c.accuracy != null && c.accuracy <= 0.50 && c.attempts >= 10) return true;
    const dir = directOf(c);
    return dir != null && dir < 0.5 && c.attempts >= 3;
  };
  // 우선순위(§3.2 — 실데이터(354·285) 캘리브레이션으로 확정, 수용 기준 통과 조합):
  //   accW = recency 비대칭 nudge — 최근K 정답률이 lifetime보다 (deadband 초과) 낮을 때만 accW를 그만큼 낮춘다
  //          (최근 하락 → 우선순위↑). 회복(최근>lifetime) 시 accW=lifetime로 강등 없음. 표본 부족·att≤K는 nudge 0.
  //          풀 게이트(inRxPool)는 lifetime 불변 — recency를 절대 반영 안 함(만성 회복 축출=2026-07-04 착시 차단, C3).
  //   W = 0.3·(1−direct) + 0.7·(1−accW)          — r(최근 상태)·정오 기록(만성+recency)의 결합, 만성 중시
  //   priority = (0.5 + 0.5·√(impact/impactMax)) × W² × (0.5 + 0.5·st)
  //  · W² — 취약도 대비 강조: 준숙달(acc 75%) 개념이 st·임팩트 가중으로 최상위를 차지하는 것 차단
  //  · 임팩트는 ±2배 유계 부스터 — √impact 원안은 노출 지배가 재발해 acc 9% 최약이 10위권 밖으로 밀림(캘리브레이션 실측)
  //  · (0.5+0.5·st) — 신뢰도(안정도) 비례(사용자 정본 유지)
  const rxPoolConcepts = concepts.filter(inRxPool);
  const impactMax = Math.max(1, ...rxPoolConcepts.map(c => c.impactScore || 0));
  const recencyAdjustedAcc = (c) => {
    const life = c.accuracy ?? 1;
    if (c.recentAcc == null) return life;                                 // 표본 부족·att≤K → nudge 없음
    const drop = Math.max(0, (life - c.recentAcc) - RX_RECENCY_DEADBAND); // 최근 하락분(회복은 0)
    return Math.max(0, life - RX_RECENCY_LAMBDA * drop);                  // 비대칭: 하락 시만 accW↓
  };
  function priority(c) {
    const s = c.stabilityNorm == null ? 0.5 : c.stabilityNorm;
    const dir = directOf(c) ?? 0.5;
    const w = 0.3 * (1 - dir) + 0.7 * (1 - recencyAdjustedAcc(c));
    return (0.5 + 0.5 * Math.sqrt((c.impactScore || 0) / impactMax)) * w * w * (0.5 + 0.5 * s);
  }
  const weakRanked = rxPoolConcepts
    .map(c => ({ ...c, priorityScore: priority(c) }))
    .sort((a, b) => b.priorityScore - a.priorityScore);

  // ── 시계열 ──
  const monthMap = {};
  const firstSeenMonth = {};
  flatAttempts.forEach(a => {
    const mk = monthKey(a.created_at);
    (monthMap[mk] ||= { month: mk, attempts: 0, correct: 0, diffs: [], concepts: new Set() });
    monthMap[mk].attempts++; if (a.correct) monthMap[mk].correct++;
    if (a.difficulty) monthMap[mk].diffs.push(a.difficulty);
    a.cids.forEach(cid => {
      monthMap[mk].concepts.add(cid);
      if (!firstSeenMonth[cid] || mk < firstSeenMonth[cid]) firstSeenMonth[cid] = mk;
    });
  });
  const monthlySeries = Object.values(monthMap).sort((a, b) => a.month < b.month ? -1 : 1).map(m => ({
    month: m.month, attempts: m.attempts, correct: m.correct,
    accuracy: safeDivide(m.correct, m.attempts),
    newConcepts: 0, avgDifficulty: mean(m.diffs),
  }));
  const newByMonth = {};
  Object.entries(firstSeenMonth).forEach(([cid, mk]) => { newByMonth[mk] = (newByMonth[mk] || 0) + 1; });
  let cum = 0;
  const cumulativeGrowth = monthlySeries.map(m => {
    m.newConcepts = newByMonth[m.month] || 0; cum += m.newConcepts;
    return { month: m.month, cumulativeLearned: cum };
  });

  const weekAttempts = {};
  flatAttempts.forEach(a => { const wk = weekKey(a.created_at); weekAttempts[wk] = (weekAttempts[wk] || 0) + 1; });
  // 기간 전체를 주 단위로 zero-fill: 무활동 주가 빠지면 '최장 연속 활동'이 활동 주 총수와 같아지고
  // 주별 차트의 시간축(주 = 연속 7일 가정)이 어긋난다. KST는 DST 없음 → 1일 스텝 = 정확히 달력 1일.
  const weeklySeries = [];
  if (attemptDates.length) {
    // 앵커 = 첫 풀이 주(계정 created_at 아님): 계정 생성 후 장기 무활동이면 선행 빈 주가 차트를 지배하고,
    // created_at이 Date 미파싱 형식이면 NaN으로 fill 루프가 통째로 죽는다. 활동 구간(첫~마지막 풀이)만 채운다.
    const fillStart = new Date(attemptDates[0]).getTime();
    const pushWeek = (ms) => {
      const wk = weekKey(ms);
      if (weeklySeries.length && weeklySeries[weeklySeries.length - 1].weekKey === wk) return;
      // startAt: 이 주의 첫 스캔 시각 — 차트 월 라벨용(weekKey가 연 경계에서 7일 미만 주를 만들 수 있어
      // '인덱스 × 7일' 산술 대신 실제 날짜가 필요).
      weeklySeries.push({ weekKey: wk, startAt: ms, attempts: weekAttempts[wk] || 0, active: (weekAttempts[wk] || 0) > 0 });
    };
    for (let t = fillStart; t <= endMs; t += 86400000) pushWeek(t);
    pushWeek(endMs); // 마지막 날이 1일 스텝에 걸리지 않아도 종료 주를 포함
  }

  // ── 최근 30일 윈도우 ──
  function windowStat(fromDays, toDays) {
    const lo = endMs - fromDays * 86400000, hi = endMs - toDays * 86400000;
    const sub = flatAttempts.filter(a => { const t = new Date(a.created_at).getTime(); return t > lo && t <= hi; });
    const cset = new Set(); sub.forEach(a => a.cids.forEach(c => cset.add(c)));
    const correct = sub.filter(a => a.correct).length;
    return { attempts: sub.length, correct, accuracy: safeDivide(correct, sub.length), activeConcepts: cset.size };
  }
  const activity30 = windowStat(30, 0);
  const activityPrev30 = windowStat(60, 30);
  const momentum = activity30.accuracy - activityPrev30.accuracy;

  // ── 스탬프 진행 ──
  const actualByStamp = {};
  genetics.stamps.forEach(s => { actualByStamp[s.stamp_id] = s.exams.length; });
  const stampProgress = examsPlanned.map(s => ({
    tree_name: s.tree_name, edu_ver: s.edu_ver, tree_id: s.tree_id, stamp_id: s.stamp_id,
    actual: actualByStamp[s.stamp_id] || 0, planned: s.exam_count || 0,
    progress: clamp(safeDivide(actualByStamp[s.stamp_id] || 0, s.exam_count || 0), 0, 1),
  })).sort((a, b) => b.planned - a.planned);

  // ── 태그 추세 ──
  const tagAgg = {};
  flatAttempts.forEach(a => {
    a.cids.forEach(cid => {
      const tag = conceptsById[cid]?.tags[0] || 'unknown';
      const t = (tagAgg[tag] ||= { tag, attempts: 0, correct: 0, recent: [0, 0], prev: [0, 0] });
      t.attempts++; if (a.correct) t.correct++;
      const recent = (endMs - new Date(a.created_at).getTime()) <= 30 * 86400000;
      t[recent ? 'recent' : 'prev'][a.correct ? 0 : 1]++;
    });
  });
  const tagTrends = Object.values(tagAgg).map(t => {
    const recentAcc = safeDivide(t.recent[0], t.recent[0] + t.recent[1], null);
    const prevAcc = safeDivide(t.prev[0], t.prev[0] + t.prev[1], null);
    const d = (recentAcc != null && prevAcc != null) ? recentAcc - prevAcc : 0;
    return { tag: t.tag, attempts: t.attempts, accuracy: safeDivide(t.correct, t.attempts), recent30Acc: recentAcc, prevAcc, arrow: d > 0.03 ? '▲' : d < -0.03 ? '▼' : '→' };
  }).sort((a, b) => b.attempts - a.attempts);

  // ── 난이도 밴드 ── (구 examTypeStats는 2026-07-04 렌더 제거로 폐기 — 소비처 0)
  const bandAgg = { easy: { count: 0, correct: 0 }, mid: { count: 0, correct: 0 }, hard: { count: 0, correct: 0 } };
  flatAttempts.forEach(a => {
    const b = a.difficulty <= 2 ? 'easy' : a.difficulty === 3 ? 'mid' : 'hard';
    bandAgg[b].count++; if (a.correct) bandAgg[b].correct++;
  });
  const difficultyBands = {};
  Object.entries(bandAgg).forEach(([k, v]) => { difficultyBands[k] = { count: v.count, accuracy: safeDivide(v.correct, v.count) }; });

  // ── 스코어 입력 ──
  const learnedNorm = concepts.filter(c => c.rStatus != null);
  const avgR = mean(learnedNorm.map(c => clamp((c.rStatus + V) / (2 * V), 0, 1)));
  // 적응력(h_status)은 원값이 v_scale 천장에 거의 닿지 않아 절대 정규화 시 과소평가됨.
  // → 관측 분포의 p90 기준 상대 스케일(hCeil — 우선순위 블록에서 정의) 사용.
  const avgH = mean(concepts.filter(c => c.hStatus != null).map(c => clamp(c.hStatus / hCeil, 0, 1)));
  const avgStab = mean(concepts.filter(c => c.stabilityNorm != null).map(c => c.stabilityNorm));
  const freshConcepts = concepts.filter(c => c.staleDays != null && c.staleDays <= 45).length;
  const freshness = safeDivide(freshConcepts, learnedCount);
  const overallAcc = safeDivide(sum(concepts.map(c => c.correct)), sum(concepts.map(c => c.attempts)));
  const weakShare = safeDivide(byBand.weak.length, learnedCount);
  const staleWeakShare = safeDivide(staleWeak.length, Math.max(byBand.weak.length, 1));

  const healthScore = clamp(calcHealthScore({ overallAcc, last30Acc: activity30.accuracy, freshness, avgR, avgStab }), 0, 100);
  const riskScore = clamp(calcRiskScore({ weakShare, staleWeakShare, avgStab }), 0, 100);
  const consistencyScore = calcConsistencyScore(monthlySeries);

  // ── 처방 로드맵 ──
  const roadmapSteps = {
    short: weakRanked.slice(0, 3),
    mid: weakRanked.slice(3, 8),
    long: tagTrends.map(t => ({ tag: t.tag, deficit: (1 - t.accuracy) * t.attempts }))
      .sort((a, b) => b.deficit - a.deficit).slice(0, 3),
  };

  // ── 클러스터 처방: 함께 자주 틀리는(pair≥3) 취약 공식 묶음 (connected components) ──
  const pool = weakRanked.slice(0, 20);
  const poolIds = new Set(pool.map(c => c.cid));
  const adj = {}; pool.forEach(c => adj[c.cid] = new Set());
  Object.entries(pairCount).forEach(([key, n]) => {
    if (n < 3) return; const [a, b] = key.split('_').map(Number);
    if (poolIds.has(a) && poolIds.has(b)) { adj[a].add(b); adj[b].add(a); }
  });
  const seen = new Set(); const clusterPlans = [];
  pool.forEach(({ cid: start }) => {
    if (seen.has(start)) return;
    const stack = [start], comp = [];
    while (stack.length) { const x = stack.pop(); if (seen.has(x)) continue; seen.add(x); comp.push(x); adj[x].forEach(y => { if (!seen.has(y)) stack.push(y); }); }
    if (comp.length >= 2) {
      const cs = comp.map(cid => conceptsById[cid]).filter(Boolean);
      const tagFreq = {}; cs.forEach(c => { const t = c.tags[0] || '혼합'; tagFreq[t] = (tagFreq[t] || 0) + 1; });
      const sharedTag = Object.entries(tagFreq).sort((a, b) => b[1] - a[1])[0][0];
      clusterPlans.push({ clusterId: 'C' + (clusterPlans.length + 1), concepts: cs, sharedTag, totalGain: sum(cs.map(c => c.gainPotential)) });
    }
  });
  clusterPlans.sort((a, b) => b.totalGain - a.totalGain).forEach((c, i) => c.clusterId = 'C' + (i + 1));

  // ── 플래그 ──
  const deltaAllZero = !engine.delta || Object.keys(engine.delta).filter(k => k.startsWith('delta_')).every(k => (engine.delta[k] || []).every(x => x === 0));
  const flags = {
    hasDelta: !deltaAllZero,
    hasLastRecord: Array.isArray(engine.last_record) && engine.last_record.some(x => Array.isArray(x) && x.length),
    hasOutdated: concepts.some(c => c.isOutdated),
  };

  // ── meta / counts ──
  const labeledCoverage = 1 - safeDivide(engine.unlabeled_problems, engine.total_problems);
  const today = new Date();
  const reportId = `KY-RPT-${today.getFullYear()}${String(today.getMonth()+1).padStart(2,'0')}${String(today.getDate()).padStart(2,'0')}-${studentCard.student_id}`;
  const meta = {
    period: { start: ymd(periodStart), end: ymd(periodEnd), days: daysBetween(periodStart, periodEnd) },
    counts: {
      actualExamCount: genetics.stamps.reduce((s, st) => s + st.exams.length, 0),
      plannedExamCount: sum(examsPlanned.map(s => s.exam_count || 0)),
      attemptCount: flatAttempts.length,
      uniquePidCount: new Set(flatAttempts.map(a => a.pid)).size,
      totalConceptSpace, learnedCount, unlearnedCount,
      labeledCoverage,
      engineProcessed: engine.solved_problems,
      recoverableWrongCount,
      almostMasteredCount: almostMastered.length,
    },
    reportId,
  };

  return {
    meta, vScale: V, studentCard, flags, flatAttempts,
    monthlySeries, weeklySeries, cumulativeGrowth,
    activity30, activityPrev30, momentum,
    healthScore, riskScore, consistencyScore,
    stampProgress, tagTrends, difficultyBands,
    conceptsById, concepts, byBand, metaMissingConceptIds,
    masteryLayers, almostMastered, recoverableWrongCount, staleWeak, totalGainPotential,
    weakRanked, roadmapSteps, clusterPlans,
    overallAcc, avgR, avgH, avgStab, freshness,
  };
}

export {
  buildReport, diagnoseDifficultyPattern, calcHealthScore, calcRiskScore,
  calcConsistencyScore, calcDifficultyCeiling, safeDivide, mean, stddev, clamp,
};

export default buildReport;
